import axios from 'axios';
import {
  showToRoles,
  downloadBlob,
  createZipFromImages,
  shareFiles,
  createFilesFromImages,
  toIsoDate,
  extendContainerValidator,
} from '../../utils';
import { ContainerGetAllQuery, ContainerType, ImageObjType, User } from '../../types';
import { containerConditionsLabel } from '../../const';

interface ContainerProps {
  containerIds?: string[];
  number?: string;
  yardId?: string;
  clientId?: string;
  status?: string;
  hideAwaitingPayment?: boolean;
  short?: boolean;
  types?: string | string[] | any;
  type?: ContainerType;
  condition?: keyof typeof containerConditionsLabel;
  disabledNumbers?: string[];
  withoutClientYardFilter?: boolean;
}

class ContainersService {
  getAll = (props: any) => axios.get<ContainerGetAllQuery>(`/containers`, props);

  getAllPreFormatProps = (props: any) => {
    const keys = props.params;

    if (
      typeof keys.number === 'string' &&
      (keys.number?.length < 3 || !extendContainerValidator(keys.number))
    )
      delete keys.number;
    if (Array.isArray(keys.number)) {
      delete keys.take;
      delete keys.skip;
    }
    if (keys.startDate) keys.startDate = toIsoDate(keys.startDate, true);
    if (keys.endDate) keys.endDate = toIsoDate(keys.endDate, true);

    return axios.get<ContainerGetAllQuery>(`/containers`, props);
  };

  get = (id: any) => axios.get(`/containers/${id}`);

  options =
    (
      { disabledNumbers, withoutClientYardFilter, ...props }: ContainerProps,
      user: User,
      onUpdate?: (data: any) => void,
    ) =>
    async () => {
      if (onUpdate) onUpdate([]);
      if (props.types) props.types = props.types.split(', ');
      const params: any = {
        ...props,
        notLoading: true,
      };
      if (!withoutClientYardFilter) {
        if (showToRoles('client', user)) params.clientId = user.clientEmployee.clientId;
        if (showToRoles('client', user) && !props.yardId) return [];
        if (!params.clientId && !props.clientId) return [];
      }

      const res = await this.getAll({
        params,
      });

      let data = res.data.data.map((container: any) => ({
        label: container.number,
        id: container.id,
        containerType: container.type || '',
        gateInDate: container?.gateIn?.completedAt || null,
        isContainerSale: !!container.sale,
        isHold: !!container.isHold,
        disabled: disabledNumbers
          ? disabledNumbers.includes(container.number) || !!container.isHold
          : !!container.isHold,
      }));

      data = data.sort((a, b) => Number(a.disabled) - Number(b.disabled));

      if (onUpdate) onUpdate(data);

      return data;
    };

  uploadImages = (id: string, photos: ImageObjType[] = []) => {
    if (!photos.length) return true;

    const formData = new FormData();
    for (const i in photos.map((obj) => obj.file)) {
      formData.append(`images`, photos[i].file);
    }

    return axios.patch(`/containers/${id}/upload-images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  deleteImages = (id: string, photos: ImageObjType[] = []) => {
    if (!photos.length) return true;

    return axios.patch(`/containers/${id}/delete-images`, { names: photos.map((obj) => obj.name) });
  };

  updateImages = async ({ id, photos = [] }: { id: string; photos: ImageObjType[] }) => {
    await this.deleteImages(
      id,
      photos.filter((obj) => obj.action === 'delete'),
    );

    return this.uploadImages(
      id,
      photos.filter((obj) => obj.action === 'upload'),
    );
  };

  downloadImages = async (images: any[], name: string) => {
    const blob = await createZipFromImages(images);
    downloadBlob(blob, `${name}.zip`);
  };

  shareImages = async (images: any[], name: string) => {
    if (images.length > 10) {
      await containersService.downloadImages(images, name);
      return;
    }

    const files = await createFilesFromImages(images);
    await shareFiles(files, name);
  };

  uploadDocuments = (id: string, documents: ImageObjType[] = []) => {
    if (!documents.length) return true;

    const formData = new FormData();
    for (const i in documents.map((obj) => obj.file)) {
      formData.append(`documents`, documents[i].file);
    }

    return axios.patch(`/containers/${id}/upload-documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  deleteDocuments = (id: string, documents: ImageObjType[] = []) => {
    if (!documents.length) return true;

    return axios.patch(`/containers/${id}/delete-documents`, {
      names: documents.map((obj) => obj.name),
    });
  };

  updateDocuments = async ({ id, documents = [] }: { id: string; documents?: ImageObjType[] }) => {
    await this.deleteDocuments(
      id,
      documents.filter((obj) => obj.action === 'delete'),
    );

    return this.uploadDocuments(
      id,
      documents.filter((obj) => obj.action === 'upload'),
    );
  };

  downloadDocument = (href: string, name: string) => {
    return axios.get(href, { responseType: 'blob' }).then((res) => {
      downloadBlob(res.data, name);
    });
  };

  viewDocument = (href: any = '') => {
    if (!href) return;
    let url = href;

    if (typeof href === 'object') url = window.URL.createObjectURL(href);

    window.open(url, '_blank');

    if (typeof href === 'object') window.URL.revokeObjectURL(url);
  };

  add = (props: any) => axios.post(`/gate-ins/now`, props);

  out = (props: any) => axios.post(`/gate-outs/now`, props);

  edit = ({ id, ...props }: any) => axios.patch(`/containers/${id}`, props);

  removePlace = (id: string) => axios.patch(`/containers/${id}/remove-place`);

  updateHold = ({ isHold, body }: any) =>
    axios.patch(`/containers/${isHold ? 'hold' : 'release-hold'}`, body);

  transfer = ({ id, ...props }: any) => axios.patch(`/containers/${id}/transfer`, props);
}

export const containersService = new ContainersService();
